import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import { PRINT_SIZES } from '../../types/cart';
import Button from '../shared/Button';
import OrderConfirmation from './OrderConfirmation';

const ShoppingCart: React.FC = () => {
  const { t } = useTranslation();
  const { items, removeFromCart, updateQuantity, updateSize, totalAmount } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);

  const handleQuantityChange = (itemId: string, delta: number) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      updateQuantity(itemId, item.quantity + delta);
    }
  };

  if (showCheckout) {
    return <OrderConfirmation onBack={() => setShowCheckout(false)} />;
  }

  return (
    <div className="min-h-screen bg-dark-primary py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={() => window.location.hash = '#home'}
            className="flex items-center text-cream-dark hover:text-cream transition-colors group"
          >
            <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('cart.backToGallery')}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl sm:text-5xl text-cream mb-4">
            {t('cart.title')}
          </h1>
          <div className="w-24 h-px bg-gold/50 mx-auto"></div>
        </div>

        {items.length === 0 ? (
          // Empty Cart
          <div className="text-center py-16">
            <svg
              className="w-24 h-24 mx-auto text-cream-dark/30 mb-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <h2 className="font-playfair text-2xl text-cream mb-4">
              {t('cart.empty')}
            </h2>
            <p className="text-cream-dark mb-8">{t('cart.emptyDescription')}</p>
            <a
              href="#preview-gallery"
              className="inline-block px-8 py-3 border border-gold/30 hover:border-gold hover:bg-gold/5 transition-all duration-300"
            >
              <span className="text-cream font-light tracking-wider uppercase text-sm">
                {t('cart.continueShopping')}
              </span>
            </a>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              <AnimatePresence>
                {items.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    className="bg-dark-surface border border-dark-border/30 rounded-lg p-6"
                  >
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                      {/* Image */}
                      <div className="w-full sm:w-32 h-40 sm:h-32 flex-shrink-0">
                        <img
                          src={item.imageSrc}
                          alt={item.imageTitle}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="font-playfair text-xl text-cream mb-2">
                            {item.imageTitle}
                          </h3>
                          
                          {/* Size Selector */}
                          <div className="flex items-center gap-2 mb-2">
                            <label className="text-cream-dark text-sm">{t('cart.size')}:</label>
                            <select
                              value={item.size.id}
                              onChange={(e) => {
                                const newSize = PRINT_SIZES.find(s => s.id === e.target.value);
                                if (newSize) updateSize(item.id, newSize);
                              }}
                              className="bg-dark-primary border border-dark-border/30 text-cream px-3 py-1 rounded focus:border-gold/50 focus:outline-none"
                            >
                              {PRINT_SIZES.map((size) => (
                                <option key={size.id} value={size.id}>
                                  {size.name} - {size.dimensions} (€{size.price})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Quantity */}
                          <div className="flex items-center gap-3">
                            <label className="text-cream-dark text-sm">{t('cart.quantity')}:</label>
                            <div className="flex items-center">
                              <button
                                onClick={() => handleQuantityChange(item.id, -1)}
                                className="w-8 h-8 rounded-l border border-dark-border/30 hover:border-gold/50 flex items-center justify-center text-cream hover:text-gold transition-colors"
                              >
                                -
                              </button>
                              <span className="w-12 h-8 border-t border-b border-dark-border/30 flex items-center justify-center text-cream">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item.id, 1)}
                                className="w-8 h-8 rounded-r border border-dark-border/30 hover:border-gold/50 flex items-center justify-center text-cream hover:text-gold transition-colors"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Price and Remove */}
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-light text-gold">€{item.price}</span>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-cream-dark hover:text-red-500 transition-colors"
                            aria-label="Remove item"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-dark-surface border border-dark-border/30 rounded-lg p-6 sticky top-24">
                <h2 className="font-playfair text-2xl text-cream mb-6">
                  {t('cart.orderSummary')}
                </h2>
                
                <div className="space-y-4 mb-6">
                  {items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-cream-dark">
                        {item.imageTitle} ({item.size.name} × {item.quantity})
                      </span>
                      <span className="text-cream">€{item.price}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-dark-border/20 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg text-cream">{t('cart.total')}</span>
                    <span className="text-3xl font-light text-gold">€{totalAmount}</span>
                  </div>
                </div>

                <Button
                  onClick={() => setShowCheckout(true)}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {t('cart.proceedToCheckout')}
                </Button>

                <a
                  href="#preview-gallery"
                  className="block text-center mt-4 text-cream-dark hover:text-cream text-sm font-light transition-colors"
                >
                  {t('cart.continueShopping')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingCart;